import { useQuery } from "@tanstack/react-query";
import { Printer } from "lucide-react";
import { Link, useOutletContext, useSearchParams } from "react-router";
import { ALLERGENS } from "../../../shared/constants";
import { NUTRIENTS } from "../../../shared/nutrition";
import type { PublishedNutrition } from "../../../shared/nutrition-addon";
import { api } from "../../api/client";
import { Alert, ICON } from "../../components/ui";
import type { ShellContext } from "../AppShell";
import { NUTRIENT_LABELS } from "../menus/LabelModal";

// Printable export of "Valori nutriționale" (PDF through the browser's "Save as PDF"; no new dependency). The
// navigation is hidden when printing (.no-print / @media print).

interface Row {
  id: string;
  category: string;
  name: string;
  portion: string;
  status: string;
  values: PublishedNutrition | null;
  missing: string[];
}

const fmt = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : String(n).replace(".", ",");
const allergenLabel = (code: string) => ALLERGENS.find(([c]) => c === code)?.[1] ?? code;

export function NutritionPrint() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const [params] = useSearchParams();
  const mid = params.get("menu") ?? "";
  const q = useQuery({
    queryKey: ["nutrition", rid, "export", mid],
    queryFn: () =>
      api<{ rows: Row[] }>("GET", `/restaurants/${rid}/nutrition/menus/${mid}/export`).then((r) => r.rows),
    enabled: !!mid,
  });
  return (
    <div className="nutrition-print">
      <style>{`@media print{.sidebar,.topbar,.bottom-nav,.no-print,header{display:none!important}.main,.main-inner{padding:0!important;margin:0!important}.nutrition-print table{font-size:10px}.nutrition-print tr{break-inside:avoid}}`}</style>
      <div className="row no-print" style={{ gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <Link to="/app/nutrition" className="btn btn-secondary btn-sm">
          ← Înapoi
        </Link>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
          <Printer {...ICON} aria-hidden="true" /> Tipărește / Salvează ca PDF
        </button>
      </div>
      <h1 className="h1">{membership.restaurant.name} — valori nutriționale</h1>
      <p className="caption" style={{ marginTop: 0 }}>
        Valori estimate, calculate din rețeta declarată și din baze de date alimentare (USDA FoodData Central;
        conține date din Open Food Facts, ODbL). Generat la {new Date().toLocaleString("ro-RO")}.
      </p>
      {q.isError && <Alert tone="error">{q.error.message}</Alert>}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
          <thead>
            <tr>
              {[
                "Produs",
                "Ingrediente (g)",
                "Alergeni",
                "E-uri",
                ...NUTRIENTS.map((n) => `${NUTRIENT_LABELS[n]} /100 g`),
                "Stare",
              ].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    borderBottom: "1px solid #999",
                    padding: "4px 6px",
                    verticalAlign: "bottom",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(q.data ?? []).map((r) => (
              <tr key={r.id} style={{ borderBottom: "1px solid #ddd", verticalAlign: "top" }}>
                <td style={{ padding: "4px 6px", minWidth: 150 }}>
                  <strong>{r.name}</strong>
                  <div className="caption muted">
                    {r.category}
                    {r.values ? ` · ${fmt(r.values.grams)} g` : r.portion ? ` · ${r.portion}` : ""}
                    {r.values?.frozen ? " · din produs congelat" : ""}
                  </div>
                </td>
                <td style={{ padding: "4px 6px", minWidth: 150 }}>
                  {r.values?.ingredients.map((x) => `${x.name} ${fmt(x.grams)}`).join(", ") ?? "—"}
                  {r.missing.length ? (
                    <div className="caption">De completat: {r.missing.join(", ")}</div>
                  ) : null}
                </td>
                <td style={{ padding: "4px 6px" }}>
                  {r.values?.allergens.map(allergenLabel).join(", ") || "—"}
                </td>
                <td style={{ padding: "4px 6px" }}>{r.values?.additives.join(", ") || "—"}</td>
                {NUTRIENTS.map((n) => (
                  <td key={n} style={{ padding: "4px 6px", textAlign: "right" }}>
                    {fmt(r.values?.per100g[n])}
                  </td>
                ))}
                <td style={{ padding: "4px 6px" }}>{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
